import React, { useEffect } from 'react'
import cn from 'classnames'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { find, get, isEmpty } from 'lodash';

import { getOrderInProgress, makeGetOrder } from '../selectors/order'
import ReceiptCard from '../components/receipt/ReceiptCard'
import QRCodeReceipt from '../containers/receipt/QRCodeReceipt'
import { clearCart } from '../actions/cart'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { ReactComponent as CloseIcon } from '../assets/icons/sidebar-modal-close.svg'

import styles from './Receipt.module.scss'
import { getIsKiosk } from '../selectors/menus';
import { getMobilePaySetting } from '../selectors/revenueCenter';
import { makeGetServiceChargeLegalText } from '../selectors/venue';

import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { bridge_setCFDScreen } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { VNBalanceDue } from '../VNCustomerFacingDisplay/containers/VNBalanceDue'
import { getCurrentPaymentFlow } from '../VNCustomerFacingDisplay/Selectors'
import { CFD_POS_PAYMENT_FLOWS } from '../VNCustomerFacingDisplay/Enums'
import { centsToDollars } from '../utils/formatters'
import { setCustomerPayingWithCard, setCustomerViewingReceiptDialog } from '../VNDialogs/ActionCreators'
import { customHistory } from '../stores/index'
import { useSelector, useDispatch } from 'react-redux'
import { Box, makeStyles } from '@material-ui/core';
import { resetCFD, setBalanceToBePaid } from '../VNCustomerFacingDisplay/ActionCreators';
import { clearOrderInProgress } from '../actions/order';
import { getPaymentProgress } from '../VNCustomerFacingDisplay/Selectors';
import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    backgroundColor: '#000000'
  },
}))

const Receipt = ({ className, order, orderNumber, isRichCheckoutOrder, serviceChargeLegal, onDone, hasBalanceDueInCents, isKiosk }) => {

  const dispatch = useDispatch()

  // SELECTORS
  // what mode are we in
  const cfdMode = useSelector(state => getDeviceMode(state))
  const paymentProgress = useSelector(state => getPaymentProgress(state))
  const currentPaymentFlow = useSelector(state => getCurrentPaymentFlow(state))
  const [resetOrderTotals] = useRemoteOrderTotal()
  
  // apply receipt styles for cfd
  const cfdStyles = useStyles()

  // navigate to the receipt screen if we are in CFD mode
  useEffect(() => {
    if (cfdMode === MODES.POS) {
      // all paid
      if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY) {

        if (parseInt(order.balanceDueInCents) <= 0) {
          dispatch(setCustomerPayingWithCard({open: false}))
          dispatch(setCustomerViewingReceiptDialog(true))
          bridge_setCFDScreen(CFD_SCREEN.RECEIPT, { cart: { uuid: order?.uuid }, transaction: { amount: (order?.amountInCents / 100)} })
        }

      } else if(currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.QR_PAY) {
        dispatch(setCustomerViewingReceiptDialog(true))
        bridge_setCFDScreen(CFD_SCREEN.RECEIPT, { cart: { uuid: order?.uuid }, transaction: { amount: (order?.amountInCents / 100)} })
      } else {
        // pure cash or custom tender
        bridge_setCFDScreen(CFD_SCREEN.RECEIPT, { cart: { uuid: order?.uuid }, transaction: { amount: (order?.amountInCents / 100)} })
      }
    }
  }, [order])

  useEffect(() => {
    if(parseInt(paymentProgress?.index) === 4) {
      orderCompleted()
    }
  }, [paymentProgress])

  useEffect(() => {
    if (cfdMode !== MODES.POS) return 
    const balanceDueInCents = parseInt(order?.balanceDueInCents || 0)

    if (balanceDueInCents > 0) {
      let totalAmountPaid = 0
      order.payments.forEach(payment => {
        totalAmountPaid += payment.amountInCents
      })

      const balanceToBePaid = {
        amountPaid: totalAmountPaid, 
        balanceDue: balanceDueInCents ?? 0
      }

      dispatch(setBalanceToBePaid(balanceToBePaid))
      bridge_setCFDScreen(CFD_SCREEN.BALANCE_DUE, { balanceToBePaid: balanceToBePaid})

      customHistory.push({
        pathname: '/pos/balance-due'
      })
    }
  }, [])

  const classNames = cn(styles.receipt, className)
  const mediaQuery = window.matchMedia("(max-width: 450px)")
  const heightSetting = { height: isKiosk ? 'auto' : '100%' }

  const richCheckoutDisclaimer = isRichCheckoutOrder ? (
    <div className={styles.disclaimer}>
      Discounts and gratuity applied from mobile purchases are not included.
      Please check your mobile receipt for final total.
    </div>
  ) : null

  const serviceChargeDisclaimer = !isKiosk && serviceChargeLegal ? (
    <div className={styles.disclaimer}>{serviceChargeLegal}</div>
  ) : null

  const orderCompleted = () => {
      // in cfd mode clear oder in progress especially for cash because because there will be an 
    // existing order and the useEffect will not work.
    if (cfdMode === MODES.POS) {
      dispatch(clearOrderInProgress())
      dispatch(resetCFD())
      resetOrderTotals()
      bridge_setCFDScreen(CFD_SCREEN.IDLE)
    }
    onDone(hasBalanceDueInCents, order)
  }

  const display = () => {
    if (currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.UNSET) {
      return <div className={cfdStyles.root}></div>
    } else {
      return (
        <div className={classNames} style={heightSetting}>
          <div className={styles.receiptContent}>
            {mediaQuery?.matches && (
            <div className={styles.receiptContentHeader}>
              <div className={styles.receiptContentTitle}>Receipt</div>
              <CloseIcon onClick={orderCompleted}/>
            </div>
            )}
            <span className={styles.separator}/>
            <div className={styles.orderNumberWrapper}>
              <div className={styles.orderNumber}>{orderNumber}</div>
            </div>
            <ReceiptCard className={styles.receiptCard} isRichCheckoutOrder={isRichCheckoutOrder} serviceChargeEnabled={serviceChargeLegal} mediaQuery={mediaQuery} />
            {!mediaQuery?.matches && richCheckoutDisclaimer}
            {!mediaQuery?.matches && serviceChargeDisclaimer}
          </div>
    
          <div className={styles.receiptQr}>
            <QRCodeReceipt className={styles.qrCodeReceipt}/>
          </div>
        </div>
      )
    }
  }

  return display()
}

const mapStateToProps = (state, ownProps) => {
  const orderUuid = get(ownProps, 'match.params.orderUuid', undefined)
  const paymentId = ownProps?.match?.params?.paymentId
  const getOrder = makeGetOrder()
  const isKiosk = getIsKiosk(state)
  
  let order = {}
  let orderNumber
  let hasBalanceDueInCents = false
  
  if (paymentId) {
    const orderInProgress = getOrderInProgress(state)
    order = !isEmpty(orderInProgress) ? orderInProgress : getOrder(state, orderUuid)
    orderNumber = find(order.payments,(payment) => payment.paymentId === paymentId)?.orderNumber
    hasBalanceDueInCents = order.balanceDueInCents !== 0
  } else {
    order = orderUuid ? getOrder(state, orderUuid) : getOrder(state, state.order.currentOrderId)
    orderNumber = order?.orderNumber
  }

  const isRichCheckoutOrder = order?.isRichCheckout
  const serviceChargeLegal = order?.serviceFee?.total > 0 && makeGetServiceChargeLegalText()(state)

  return { order, orderNumber, isRichCheckoutOrder, serviceChargeLegal, hasBalanceDueInCents, isKiosk }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const push = get(ownProps, 'history.push', () => {})

  return {
    onDone: (hasBalanceDueInCents, order) => {
      if (hasBalanceDueInCents) {
        push({
          pathname: '/split-tender',
          state: { order }
        })
      } else {
        dispatch(clearCart())
        dispatch(clearPromotionsOnOrder())
        push('/concession-order')
      }
    }
  }
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Receipt))
